import { useEffect, useRef } from "react";

/**
 * Background "signal field": sparse glyphs flicker on a monospace grid while packets of
 * bits travel along rows and columns, leaving fading trails. Very low contrast so it reads
 * as texture; pauses when the tab is hidden and renders a static frame for reduced motion.
 */
const GLYPHS = "·.:+-=/\\|01x#%░";
const CELL_W = 14;
const CELL_H = 22;

type Lit = { c: number; r: number; ch: string; life: number; max: number; tint: 0 | 1 | 2 };
type Packet = { c: number; r: number; dc: number; dr: number; len: number; ttl: number };

export function SignalField() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let cols = 0;
    let rows = 0;
    let lit: Lit[] = [];
    let packets: Packet[] = [];
    let raf = 0;
    let last = 0;
    const mouse = { x: -1e4, y: -1e4 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `12px "IBM Plex Mono", ui-monospace, monospace`;
      ctx.textBaseline = "top";
      cols = Math.ceil(window.innerWidth / CELL_W);
      rows = Math.ceil(window.innerHeight / CELL_H);
    };

    const rand = (n: number) => Math.floor(Math.random() * n);
    const glyph = () => GLYPHS[rand(GLYPHS.length)]!;

    const spawnLit = (count: number) => {
      for (let i = 0; i < count; i++) {
        const max = 40 + rand(160);
        lit.push({ c: rand(cols), r: rand(rows), ch: glyph(), life: max, max, tint: 0 });
      }
    };

    const spawnPacket = () => {
      const horizontal = Math.random() < 0.6;
      packets.push(
        horizontal
          ? { c: Math.random() < 0.5 ? 0 : cols - 1, r: rand(rows), dc: Math.random() < 0.5 ? 1 : -1, dr: 0, len: 6 + rand(14), ttl: cols }
          : { c: rand(cols), r: Math.random() < 0.5 ? 0 : rows - 1, dc: 0, dr: Math.random() < 0.5 ? 1 : -1, len: 4 + rand(8), ttl: rows },
      );
      const p = packets[packets.length - 1]!;
      if (p.dc === -1) p.c = cols - 1;
      if (p.dr === -1) p.r = rows - 1;
    };

    const draw = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const l of lit) {
        const x = l.c * CELL_W;
        const y = l.r * CELL_H;
        const fade = Math.sin((l.life / l.max) * Math.PI);
        const near = Math.hypot(mouse.x - x, mouse.y - y) < 120 ? 1.8 : 1;
        if (l.tint === 2) ctx.fillStyle = `rgba(84,53,255,${(0.55 * fade).toFixed(3)})`;
        else if (l.tint === 1) ctx.fillStyle = `rgba(79,122,0,${(0.5 * fade).toFixed(3)})`;
        else ctx.fillStyle = `rgba(106,111,135,${Math.min(0.5, 0.22 * fade * near).toFixed(3)})`;
        ctx.fillText(l.ch, x, y);
      }
    };

    const step = () => {
      for (const l of lit) l.life -= 1;
      lit = lit.filter((l) => l.life > 0);
      if (lit.length < (cols * rows) / 18) spawnLit(6);

      for (const p of packets) {
        p.c += p.dc;
        p.r += p.dr;
        p.ttl -= 1;
        const head = Math.random() < 0.08 ? 2 : 1;
        lit.push({ c: p.c, r: p.r, ch: Math.random() < 0.5 ? "1" : "0", life: p.len * 3, max: p.len * 3, tint: head as 1 | 2 });
      }
      packets = packets.filter((p) => p.ttl > 0 && p.c >= 0 && p.c < cols && p.r >= 0 && p.r < rows);
      if (packets.length < 3 && Math.random() < 0.04) spawnPacket();
    };

    const loop = (t: number) => {
      raf = requestAnimationFrame(loop);
      if (t - last < 60) return; // ~16 fps is plenty for texture
      last = t;
      step();
      draw();
    };

    const onMove = (e: PointerEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden && !reduced) raf = requestAnimationFrame(loop);
    };

    resize();
    spawnLit(Math.floor((cols * rows) / 22));
    if (reduced) {
      draw();
    } else {
      raf = requestAnimationFrame(loop);
    }
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={ref} className="signal-field" aria-hidden="true" />;
}
